/**
 * Shot Launcher panel — create, display, and trigger shots
 */
import { shotStore } from '../stores/shotStore';
import { projectStore } from '../stores/projectStore';
import { selectionStore } from '../stores/selectionStore';
import { monitorStore } from '../stores/monitorStore';
import { templateRegistry } from '../engine/templateRegistry';
import { executeTake } from '../engine/transitionEngine';
import { eventBus } from '../engine/eventBus';

let panelBody: HTMLElement;
let zoomLine: HTMLElement;

export function initShotLauncher(): void {
  panelBody = document.querySelector('.shot-launcher .panel-body')!;
  zoomLine = document.querySelector('.shot-zoom-line')!;

  // Set up drop zone
  setupDropZone();

  // Zoom slider
  setupZoomSlider();

  // Edit button
  const editBtn = document.querySelector('.shot-edit-btn');
  editBtn?.addEventListener('click', editSelectedShot);

  // Subscribe to store
  shotStore.subscribe(render);

  // Listen for shot triggers
  eventBus.on('shot:triggered', (payload) => {
    const shot = shotStore.getState().shots.find((s) => s.id === payload.shotId);
    if (!shot) return;
    // Execute the shot — show/hide items on program
    for (const shotItem of shot.items) {
      if (shotItem.action === 'show') {
        monitorStore.getState().setPreview(shotItem.projectItemId);
        executeTake();
      }
    }
  });

  render();
}

function setupDropZone(): void {
  panelBody.addEventListener('dragover', (e) => {
    const dt = (e as DragEvent).dataTransfer;
    if (dt?.types.includes('application/x-template-id') || dt?.types.includes('application/x-project-item-id')) {
      e.preventDefault();
      dt.dropEffect = 'copy';
      panelBody.classList.add('drop-hover');
    }
  });

  panelBody.addEventListener('dragleave', (e) => {
    if (!panelBody.contains((e as DragEvent).relatedTarget as Node)) {
      panelBody.classList.remove('drop-hover');
    }
  });

  panelBody.addEventListener('drop', (e) => {
    e.preventDefault();
    panelBody.classList.remove('drop-hover');
    const dt = (e as DragEvent).dataTransfer;

    const templateId = dt?.getData('application/x-template-id');
    if (templateId) {
      createShotFromTemplate(templateId);
      return;
    }

    const projectItemId = dt?.getData('application/x-project-item-id');
    if (projectItemId) {
      createShotFromProjectItem(projectItemId);
    }
  });

  // Double-click for new empty shot
  panelBody.addEventListener('dblclick', (e) => {
    if ((e.target as HTMLElement).classList.contains('shot-empty') ||
        (e.target as HTMLElement).classList.contains('shot-empty-text')) {
      // If we have a selected template, create a shot from it
      const sel = selectionStore.getState().current;
      if (sel.type === 'template') {
        createShotFromTemplate(sel.templateId);
      } else if (sel.type === 'projectItem') {
        createShotFromProjectItem(sel.itemId);
      }
    }
  });
}

function createShotFromTemplate(templateId: string): void {
  const template = templateRegistry.getTemplate(templateId);
  if (!template) return;

  // First add to project
  const item = projectStore.getState().addItem({
    templateId,
    name: template.name,
    fieldValues: templateRegistry.getDefaultFieldValues(templateId),
    duration: template.duration ?? 0,
    isActive: false,
  });

  // Then create shot referencing the project item
  shotStore.getState().addShot(template.name, [
    { projectItemId: item.id, action: 'show' },
  ]);
}

function createShotFromProjectItem(projectItemId: string): void {
  const item = projectStore.getState().getItem(projectItemId);
  if (!item) return;

  shotStore.getState().addShot(item.name, [
    { projectItemId, action: 'show' },
  ]);
}

function render(): void {
  const { shots, selectedShotId, zoomLevel } = shotStore.getState();

  if (shots.length === 0) {
    panelBody.innerHTML = `<div class="shot-empty">
      <span class="shot-empty-text">Drop media here or double-click for a new shot</span>
    </div>`;
    panelBody.style.display = 'flex';
    return;
  }

  panelBody.style.display = 'block';
  const size = 60 + zoomLevel * 80; // 60px to 140px

  panelBody.innerHTML = `<div class="shot-grid" style="--shot-size:${size}px;">
    ${shots.map((shot) => {
      const isSelected = shot.id === selectedShotId;
      return `<div class="shot-card${isSelected ? ' selected' : ''}" data-shot-id="${shot.id}">
        <div class="shot-card-thumb">
          <span class="shot-card-icon">&#x25B6;</span>
        </div>
        <span class="shot-card-name">${shot.name}</span>
      </div>`;
    }).join('')}
  </div>`;

  // Attach handlers
  panelBody.querySelectorAll<HTMLDivElement>('.shot-card').forEach((card) => {
    const shotId = card.dataset.shotId!;

    card.addEventListener('click', () => {
      shotStore.getState().selectShot(shotId);
      selectionStore.getState().select({ type: 'shot', shotId });
    });

    card.addEventListener('dblclick', () => {
      shotStore.getState().triggerShot(shotId);
    });
  });
}

function setupZoomSlider(): void {
  zoomLine.style.cursor = 'pointer';
  zoomLine.addEventListener('click', (e) => {
    const rect = zoomLine.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    shotStore.getState().setZoom(pct);
  });
}

function editSelectedShot(): void {
  // Placeholder — will be enhanced in Step 10
  const shotId = shotStore.getState().selectedShotId;
  if (!shotId) return;
  console.debug('Edit shot:', shotId);
}
